import type { ICollectionTargetFilters } from "@agrigate/commodity-transaction-api/lib/services/CollectionTargetsService";
import {
    alphabeticRegex,
    numericIdRegex,
    phoneNumberRegex,
    poExternalIdRegex,
} from "@agrigate/shared/lib/consts/constants";
import { type IdFmtType, parsePubId } from "@agrigate/shared/lib/consts/ids";
import { bottomToaster } from "@agrigate/shared-web-ui/lib/components/ui/toaster";
import type { IUserFilters } from "@agrigate/user-management-api/lib/services/UsersService";
import {
    Combobox,
    Portal,
    Text,
    useCombobox,
    useListCollection,
} from "@chakra-ui/react";
import { type ReactElement, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { routesConfig } from "../config/global-search-config";

const ID_ROUTE_MAP: Partial<Record<IdFmtType, (id: string) => string>> = {
    OUTPUT_BUYER: id => `/buyer-orders/details?id=${id}`,
    OUTPUT_SELLER: id => `/seller-orders/details?pbId=${id}`,
    USER: id => `/users/details?userId=${id}`,
    SHIPMENT: id => `/shipments/details?id=${id}`,
    TARGET: id => `/procurement/details?id=${id}`,
    SOIL_TESTING_BOOKING: id => `/soil-testing/bookings?bookingId=${id}`,
    MARKETPLACE_BOOKING: id => `/marketplace/orders?mpo-id=${id}`,
};

const GlobalSearch = (p: { width?: string }): ReactElement => {
    const [open, setOpen] = useState(false);
    const [inputVal, setInputVal] = useState<string>("");
    const navigate = useNavigate();

    const { collection, set: setCollectionItems } = useListCollection({
        initialItems: routesConfig,
    });

    const dynamicActions = useMemo(() => {
        const val = inputVal.toUpperCase().trim();
        const actions: Array<{ label: string; value: string }> = [];

        const matchingRoutes = routesConfig.filter(route =>
            route.label.toUpperCase().includes(val),
        );
        actions.push(...matchingRoutes);

        if (!val) return actions;

        if (numericIdRegex.test(val)) {
            actions.push(
                {
                    label: `Buyer Order: #${val}`,
                    value: `/buyer-orders/details?id=${val}`,
                },
                {
                    label: `Farmer Lot: #${val}`,
                    value: `/seller-orders/details?pbId=${val}`,
                },
                {
                    label: `User: #${val}`,
                    value: `/users/details?userId=${val}`,
                },
                {
                    label: `Shipment: #${val}`,
                    value: `/shipments/details?id=${val}`,
                },
                {
                    label: `Procurement Order: #${val}`,
                    value: `/procurement/details?id=${val}`,
                },
                {
                    label: `Soil Testing Booking: #${val}`,
                    value: `/soil-testing/bookings?bookingId=${val}`,
                },
                {
                    label: `Marketplace Order: #${val}`,
                    value: `/marketplace/orders?mpo-id=${val}`,
                },
                {
                    label: `Insurance Application: #${val}`,
                    value: `/insurance/applications?applicationId=${val}`,
                },
            );
        } else if (phoneNumberRegex.test(val)) {
            const filter: IUserFilters = { phone: { eq: val } };
            actions.push({
                label: `User by Phone: ${val}`,
                value: `/users?f=${encodeURIComponent(JSON.stringify(filter))}`,
            });
        } else if (poExternalIdRegex.test(val)) {
            const filter: ICollectionTargetFilters = {
                externalId: { eq: val },
            };
            actions.push({
                label: `Procurement Order (External Id): ${val}`,
                value: `/procurement/list?f=${encodeURIComponent(JSON.stringify(filter))}`,
            });
        } else if (alphabeticRegex.test(val)) {
            const filter: IUserFilters = { fullName: { in: [val] } };
            actions.push({
                label: `User by Name: ${val}`,
                value: `/users?f=${encodeURIComponent(JSON.stringify(filter))}`,
            });
        }

        return actions;
    }, [inputVal]);

    useEffect(() => {
        if (dynamicActions.length) setCollectionItems(dynamicActions);
        else setCollectionItems([]);
    }, [dynamicActions, setCollectionItems]);

    const handleForceNavigate = () => {
        if (inputVal.length < 9) {
            bottomToaster.create({
                title: "Invalid ID Submitted",
                type: "error",
                closable: true,
            });
            return;
        }
        setOpen(false);

        const cleanInput = inputVal.toUpperCase();

        const parsedId = parsePubId(cleanInput);
        const id = parsedId?.fmt.extractIfVal(cleanInput);

        const routeGenerator = parsedId?.type
            ? ID_ROUTE_MAP[parsedId.type]
            : null;

        if (id && routeGenerator) {
            navigate(routeGenerator(id));
        } else {
            console.warn("Failed to parse ID:", cleanInput, parsedId);
            bottomToaster.create({
                title: "Unsupported ID provided",
                type: "error",
                closable: true,
            });
        }
    };
    const combobox = useCombobox({
        collection,
        placeholder: "Search",
        inputValue: inputVal,
        onInputValueChange: e => setInputVal(e.inputValue),
        onValueChange: e => {
            if (e.value[0]) {
                navigate(e.value[0]);
            }
        },
        open,
        onOpenChange: e => setOpen(e.open),
    });

    return (
        <Combobox.RootProvider
            value={combobox}
            width={p.width ?? "320px"}
            onKeyDown={e => {
                if (e.key === "Enter" && collection.items.length === 0) {
                    e.preventDefault();
                    handleForceNavigate();
                }
            }}
        >
            <Combobox.Control>
                <Combobox.Input
                    textTransform="uppercase"
                    placeholder="Type to search"
                    _placeholder={{ textTransform: "none" }}
                />
                <Combobox.IndicatorGroup>
                    <Combobox.ClearTrigger />
                    <Combobox.Trigger />
                </Combobox.IndicatorGroup>
            </Combobox.Control>
            <Portal>
                <Combobox.Positioner>
                    <Combobox.Content>
                        <Combobox.Empty
                            cursor="pointer"
                            onClick={handleForceNavigate}
                        >
                            <Text>{`Navigate To: ${inputVal}`}</Text>
                        </Combobox.Empty>
                        {collection.items.map(item => (
                            <Combobox.Item item={item} key={item.value}>
                                {item.label}
                                <Combobox.ItemIndicator />
                            </Combobox.Item>
                        ))}
                    </Combobox.Content>
                </Combobox.Positioner>
            </Portal>
        </Combobox.RootProvider>
    );
};

export default GlobalSearch;
